import React from 'react';
import { FaExclamationTriangle, FaRedo, FaBug } from 'react-icons/fa';
import '../styles/core.css';

class ModalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log to console for development
    console.error('Modal Error Boundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString()
    });
    
    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error, { extra: errorInfo });
    }
  }

  handleRetry = () => {
    // Reset error state to retry rendering
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    });
  };

  handleReportBug = () => {
    // TODO: Open bug report modal or redirect to issue tracker
    const bugReport = {
      errorId: this.state.errorId,
      error: this.state.error?.message,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };
    
    console.log('Bug report data:', bugReport);
    // Example: window.open(`https://github.com/yourorg/yourrepo/issues/new?title=Modal Error: ${encodeURIComponent(this.state.error?.message)}`);
  };

  render() {
    if (this.state.hasError) {
      const { componentName = 'Modal Component' } = this.props;
      
      return (
        <div className="modal-error-boundary">
          <div className="error-content">
            <div className="error-icon">
              <FaExclamationTriangle />
            </div>
            
            <div className="error-message">
              <h3>Something went wrong</h3>
              <p>
                The {componentName.toLowerCase()} encountered an unexpected error and couldn't display properly.
              </p>
              
              {this.state.errorId && (
                <div className="error-id">
                  <small>Error ID: {this.state.errorId}</small>
                </div>
              )}
            </div>
            
            <div className="error-actions">
              <button 
                className="btn-primary"
                onClick={this.handleRetry}
                title="Try to reload the component"
              >
                <FaRedo /> Try Again
              </button>
              
              <button 
                className="btn-secondary"
                onClick={this.handleReportBug}
                title="Report this issue to help us improve"
              >
                <FaBug /> Report Issue
              </button>
            </div>
            
            {/* Development-only error details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Developer Details</summary>
                <div className="error-stack">
                  <h4>Error Message:</h4>
                  <pre>{this.state.error.toString()}</pre>
                  
                  <h4>Component Stack:</h4>
                  <pre>{this.state.errorInfo?.componentStack}</pre>
                  
                  <h4>Error Stack:</h4>
                  <pre>{this.state.error.stack}</pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ModalErrorBoundary;
